import { useEffect, useRef } from 'react';

import { Button } from '@playbooks/ui/components/button';
import { Icon } from '@playbooks/ui/components/icon';
import { AudioRecorder, AudioTimeline } from '@playbooks/wrappers/audio';

import { ComposerActions } from 'components/composer/composer-actions';
import { useStore } from 'contexts';
import { useQuery } from 'hooks';
import { transcribeAgentAudio } from 'services/agent/agent-transcription';

const ComposerVoiceContent = ({ onCancel, onComplete, onTranscript, recorder }) => {
	const accepting = useRef(false);
	const started = useRef(false);
	const store = useStore();

	// Actions
	const [fetchData, loading, error] = useQuery(onTranscribe);

	// Hooks
	useEffect(() => {
		if (!recorder || started.current) return;
		started.current = true;
		recorder.startRecording();
	}, [recorder]);
	useEffect(() => {
		if (accepting.current && recorder?.recordingBlob) fetchData(recorder.recordingBlob);
	}, [recorder?.recordingBlob]);
	useEffect(() => {
		if (error) onCancel();
	}, [error]);

	// Methods
	const onDiscard = () => {
		accepting.current = false;
		recorder?.cancelRecording();
		onCancel();
	};
	const onConfirm = () => {
		accepting.current = true;
		recorder.stopRecording();
	};
	async function onTranscribe(blob) {
		const transcript = await transcribeAgentAudio({ blob, store });
		onTranscript(transcript);
		onComplete();
	}

	// Render
	return (
		<>
			<div className='px-3 pb-1 pt-3'>
				<div className='flex-start min-h-[112px] text-foreground-subtle'>
					{loading ? (
						<span className='text-sm text-foreground-body'>Transcribing audio...</span>
					) : (
						<AudioTimeline active={recorder?.isRecording} stream={recorder?.stream} />
					)}
				</div>
			</div>
			<footer className='flex-between gap-3 px-3 pb-3'>
				<div className='flex-start gap-2'>
					<ComposerActions disabled />
				</div>
				<div className='flex-start gap-2'>
					<Button
						variant='outline'
						type='button'
						size='icon-xs'
						aria-label='Cancel audio'
						onClick={onDiscard}
						className='text-danger'>
						<Icon data-icon='inline-start' type='far' icon='xmark' aria-hidden />
					</Button>
					<Button
						variant='outline'
						type='button'
						disabled={!recorder || loading}
						size='icon-xs'
						aria-label='Accept audio'
						onClick={onConfirm}>
						<Icon data-icon='inline-start' type='far' icon={loading ? null : 'check'} aria-hidden />
					</Button>
				</div>
			</footer>
		</>
	);
};
const ComposerVoice = ({
	onCancel,
	onComplete,
	onError,
	onTranscript,
	setValue = (_nextValue?: string) => undefined,
	value = '',
}) => {
	const handleTranscript = transcript => {
		if (onTranscript) {
			onTranscript(transcript);
			return;
		}
		setValue(value ? `${value.trimEnd()} ${transcript}` : transcript);
	};
	return (
		<AudioRecorder onError={onError}>
			{recorder => (
				<ComposerVoiceContent
					recorder={recorder}
					onCancel={onCancel}
					onComplete={onComplete}
					onTranscript={handleTranscript}
				/>
			)}
		</AudioRecorder>
	);
};
export { ComposerVoice };
